/**
 * Prompt Conversion Utilities
 *
 * Converts TipTap JSON content to the text format that the AI will see.
 * Used by both frontend (preview) and backend (runtime execution).
 */

import type { JSONContent } from "@tiptap/core";

import { DEFAULT_DEPLOYMENT_TYPE, PROMPT_NODE_NAMES as NODE_NAMES } from "./agent-prompt.constants";
import type { TipTapJSONContent } from "./agent-prompt.types";
import { formatPlaceholderOutput, formatVariablePlaceholder } from "./agent-prompt.utils";
import { formatKnowledgePlaceholder } from "./agent-prompt-knowledge.utils";
import { createOrderedListCounters } from "./agent-prompt-markdown.constants";

export function getTabPanelDeploymentTypes(
  attrs: Record<string, unknown> | undefined
): readonly string[] {
  if (!attrs) return [];
  if (Array.isArray(attrs.deploymentTypes))
    return (attrs.deploymentTypes as unknown[]).filter((t): t is string => typeof t === "string");
  return [];
}

/**
 * Checks if a node is an empty paragraph (no content or only hardBreaks/empty text).
 * Shared utility used by both the markdown serializer and the AI text converter.
 *
 * @param node - A TipTap JSON content node to check
 * @returns `true` if the node is a paragraph with no meaningful content
 */
export function isEmptyParagraph(node: TipTapJSONContent): boolean {
  if (node.type !== NODE_NAMES.PARAGRAPH) return false;
  if (!node.content || node.content.length === 0) return true;
  // Check if all children are empty or just hardBreaks
  return node.content.every(
    (child) =>
      child.type === NODE_NAMES.HARD_BREAK || (child.type === NODE_NAMES.TEXT && !child.text)
  );
}

/**
 * Extracts plain text from inline content (text nodes).
 * Text after "//" is excluded (treated as comment) only if:
 * - "//" is at the start of the text, OR
 * - "//" is preceded by a space
 *
 * @param content - Inline content nodes
 * @param variableValues - Optional map of variableName → runtime value
 */
function extractInlineText(
  content: JSONContent[] | undefined,
  variableValues?: Map<string, string>
): string {
  if (!content) return "";

  const fullText = content
    .map((node) => {
      if (node.type === NODE_NAMES.TEXT) {
        const text = node.text || "";
        const hasCodeMark = node.marks?.some((m) => m.type === "code");
        return hasCodeMark ? `\`${text}\`` : text;
      }
      if (node.type === NODE_NAMES.HARD_BREAK) return "\n";

      // Handle inline variable mentions.
      //
      // `isValid` is stamped at parse time against the `AVAILABLE_VARIABLES`
      // registry. A `false` value means the parser didn't recognize the name —
      // typical when the user types `{{document_title}}` as a prose example
      // or escapes underscores in markdown (`document\_title`). The OLD path
      // returned `""` for these, corrupting the surrounding sentence — the
      // same failure class as the prior skill-mention silent-strip.
      //
      // New contract: never silently drop authored text. Render the literal
      // `{{name}}` so the surrounding sentence stays coherent. The downstream
      // runtime won't have a value for an unknown name, but a visible
      // placeholder is debuggable; a vanished mention is not.
      if (node.type === NODE_NAMES.VARIABLE_MENTION) {
        const variableName = node.attrs?.variableName || "";
        if (!variableName) return "";

        // Unknown / never-resolvable variable → emit the literal placeholder.
        if (node.attrs?.isValid === false) {
          return formatVariablePlaceholder(variableName);
        }

        if (variableValues) {
          // Backend mode: replace with actual value.
          if (variableValues.has(variableName)) {
            return variableValues.get(variableName) || "";
          }
          // Known variable but no runtime value (e.g. an inbox-only variable
          // referenced from a phone call). Preserve the debug-visible marker
          // so the customer can spot the mismatch in transcripts.
          return `[Variable ${variableName} not available]`;
        }

        // Frontend preview mode: show placeholder
        return formatVariablePlaceholder(variableName);
      }

      // Handle inline placeholder nodes
      if (node.type === NODE_NAMES.PLACEHOLDER_MENTION) {
        const text = node.attrs?.text || "";
        return formatPlaceholderOutput(text);
      }

      // Handle inline skill mentions.
      // Render as @name so the literal text the user authored survives into the
      // runtime prompt. The leading @ matches the markdown serializer and the
      // way mentions appear in the editor — dropping it makes "use @search_faq_fr"
      // read as "use search_faq_fr" at runtime, which mismatches the rest of the
      // prompt the user wrote.
      //
      // isNotFound/isActive are stored at the time the prompt was last saved
      // and are NEVER reconciled with the agent's current toolset. A stale
      // false on those flags must not silently blank the mention — that turns
      // "use @search_faq_fr for French" into "use  for French" and corrupts
      // adjacent instructions. Always emit @name; the live tool list passed
      // to the LLM is the source of truth for which functions actually exist.
      if (node.type === NODE_NAMES.SKILL_MENTION) {
        const skillName = node.attrs?.name || "";
        return skillName ? `@${skillName}` : "";
      }

      if (node.content) return extractInlineText(node.content, variableValues);
      return "";
    })
    .join("");

  // Exclude text after "//" (comment syntax) only if at start or preceded by space
  let commentIndex = -1;

  if (fullText.startsWith("//")) {
    commentIndex = 0;
  } else {
    commentIndex = fullText.indexOf(" //");
    if (commentIndex !== -1) {
      commentIndex = commentIndex + 1;
    }
  }

  if (commentIndex !== -1) {
    return fullText.substring(0, commentIndex).trimEnd();
  }

  return fullText;
}

/**
 * Ordered list counters using ")" delimiter (e.g., "1)", "a)", "i)")
 * Shared logic via createOrderedListCounters from agent-prompt-markdown.constants
 */
const ORDERED_LIST_COUNTERS = createOrderedListCounters(")");

/**
 * Converts a list to text with proper formatting
 * @param node - The list node (bulletList, orderedList, or taskList)
 * @param indent - Current indentation level
 * @param ragResults - Optional RAG results
 * @param variableValues - Optional variable values
 * @param parentOrderedListType - Inherited type from parent ordered list (for nested lists)
 */
function convertListToText(
  node: JSONContent,
  indent: number = 0,
  ragResults?: Map<string, string>,
  variableValues?: Map<string, string>,
  parentOrderedListType?: string,
  collectionToolMap?: Map<string, string>
): string {
  if (!node.content) return "";

  const indentStr = "  ".repeat(indent);
  const listType = node.type;
  // Nested ordered lists inherit parent's type if they don't have their own
  const orderedListType = node.attrs?.type || parentOrderedListType || "1";

  return node.content
    .map((item, index) => {
      if (item.type !== NODE_NAMES.LIST_ITEM && item.type !== NODE_NAMES.TASK_ITEM) {
        return "";
      }

      const textParts: string[] = [];
      const nestedLists: JSONContent[] = [];

      for (const child of item.content || []) {
        if (
          child.type === NODE_NAMES.BULLET_LIST ||
          child.type === NODE_NAMES.ORDERED_LIST ||
          child.type === NODE_NAMES.TASK_LIST
        ) {
          nestedLists.push(child);
        } else if (child.type === NODE_NAMES.PARAGRAPH) {
          const text = extractInlineText(child.content, variableValues);
          if (text) textParts.push(text);
        } else if (child.content) {
          const text = convertBlocksToText([child], ragResults, variableValues, collectionToolMap);
          if (text) textParts.push(text);
        }
      }

      const itemText = textParts.join(" ");

      let prefix: string;
      if (listType === NODE_NAMES.BULLET_LIST) {
        prefix = "-";
      } else if (listType === NODE_NAMES.ORDERED_LIST) {
        const defaultCounter = (n: number) => `${n})`;
        const counter =
          ORDERED_LIST_COUNTERS[orderedListType] ?? ORDERED_LIST_COUNTERS["1"] ?? defaultCounter;
        const start = (node.attrs?.start as number) || 1;
        prefix = counter(start + index);
      } else if (listType === NODE_NAMES.TASK_LIST) {
        const isChecked = item.attrs?.checked === true;
        prefix = isChecked ? "[x]" : "[ ]";
      } else {
        prefix = "-";
      }

      const formattedItem = `${indentStr}${prefix} ${itemText}`;

      // Process nested lists - pass orderedListType so nested ordered lists inherit parent's type
      const nestedText = nestedLists
        .map((nestedList) =>
          convertListToText(
            nestedList,
            indent + 1,
            ragResults,
            variableValues,
            listType === NODE_NAMES.ORDERED_LIST ? orderedListType : undefined,
            collectionToolMap
          )
        )
        .filter(Boolean)
        .join("\n");

      return nestedText ? `${formattedItem}\n${nestedText}` : formattedItem;
    })
    .filter(Boolean)
    .join("\n");
}

/**
 * Converts a single block node to text (excluding empty paragraphs)
 */
function convertSingleBlockToText(
  node: JSONContent,
  ragResults?: Map<string, string>,
  variableValues?: Map<string, string>,
  collectionToolMap?: Map<string, string>
): string | null {
  switch (node.type) {
    case NODE_NAMES.PARAGRAPH:
      return extractInlineText(node.content, variableValues);

    case NODE_NAMES.BULLET_LIST:
    case NODE_NAMES.ORDERED_LIST:
    case NODE_NAMES.TASK_LIST:
      return convertListToText(node, 0, ragResults, variableValues, undefined, collectionToolMap);

    case NODE_NAMES.BLOCKQUOTE: {
      const blockquoteText = convertBlocksToText(
        node.content,
        ragResults,
        variableValues,
        collectionToolMap
      );
      return `"${blockquoteText}"`;
    }

    case NODE_NAMES.HEADING: {
      const level = (node.attrs?.level as number) || 1;
      const prefix = "#".repeat(level);
      const headingText = extractInlineText(node.content, variableValues);
      return `${prefix} ${headingText}`;
    }

    case NODE_NAMES.CODE_BLOCK: {
      const language = (node.attrs?.language as string) || "";
      const codeText = (node.content ?? []).map((c) => c.text ?? "").join("");
      return language ? `\`\`\`${language}\n${codeText}\n\`\`\`` : `\`\`\`\n${codeText}\n\`\`\``;
    }

    case NODE_NAMES.COLLECTION_MENTION: {
      // Live data (ragResults / collectionToolMap) decides what survives.
      // The stored isActive/isNotFound flags are baked at save time and never
      // reconciled — they cannot drive runtime decisions.
      const collectionName = node.attrs?.name || "";

      // 1. Chat with RAG context: the collection block is replaced inline
      //    with vector-search results computed against the user's query.
      //    This is the original "knowledge block" semantic.
      if (ragResults) {
        if (ragResults.has(collectionName)) {
          return ragResults.get(collectionName) || "";
        } else {
          return "[Collection not available]";
        }
      }

      // 2. Voice runtime: the collection block ALWAYS drops. There is no
      //    user query at session start, so no inline RAG content can be
      //    produced. The model already learns the search tool exists via
      //    the agent's auto-registered `search_<slug>` function and any
      //    `@search_<slug>` skill mention the author placed in the prompt
      //    — re-stating it here in prose is duplicate noise and breaks
      //    the surrounding sentence grammar wherever the author embedded
      //    the block inline.
      if (collectionToolMap) return null;

      // 3. Editor preview mode (no live maps supplied): show the
      //    placeholder so the author sees the block they wrote.
      return formatKnowledgePlaceholder(collectionName);
    }

    default:
      if (node.content) {
        return convertBlocksToText(node.content, ragResults, variableValues, collectionToolMap);
      }
      return null;
  }
}

/**
 * Converts block content (paragraphs, lists, etc.) to text
 *
 * Empty paragraphs are preserved as additional newlines to maintain
 * intentional blank lines from the editor.
 */
function convertBlocksToText(
  content: JSONContent[] | undefined,
  ragResults?: Map<string, string>,
  variableValues?: Map<string, string>,
  collectionToolMap?: Map<string, string>
): string {
  if (!content) return "";

  const results: string[] = [];
  let trailingNewlines = 0;

  for (const node of content) {
    if (isEmptyParagraph(node)) {
      // Empty paragraph = one additional blank line
      trailingNewlines++;
    } else {
      const serialized = convertSingleBlockToText(
        node,
        ragResults,
        variableValues,
        collectionToolMap
      );
      if (serialized !== null && serialized !== "") {
        // Add pending empty lines to the previous block
        if (trailingNewlines > 0 && results.length > 0) {
          results[results.length - 1] += "\n".repeat(trailingNewlines);
        }
        results.push(serialized);
        trailingNewlines = 0;
      }
    }
  }

  // Add trailing empty paragraphs to the last block
  if (trailingNewlines > 0 && results.length > 0) {
    results[results.length - 1] += "\n".repeat(trailingNewlines);
  }

  return results.join("\n\n");
}

/**
 * Converts a section node to formatted text
 *
 * For sections with tabs (new architecture where tabContainer is always present):
 * - If isDeploymentSpecific: false → use NEXUS tab content only
 * - If isDeploymentSpecific: true → use selectedDeployment tab content
 */
function convertSectionToText(
  section: JSONContent,
  selectedDeployment: string,
  ragResults?: Map<string, string>,
  variableValues?: Map<string, string>,
  collectionToolMap?: Map<string, string>
): string {
  if (!section.content) {
    return "";
  }

  const isDeploymentSpecific = section.attrs?.isDeploymentSpecific === true;

  for (const child of section.content) {
    if (child.type === NODE_NAMES.SECTION_TITLE) {
      continue;
    }

    if (child.type === NODE_NAMES.TAB_CONTAINER) {
      const targetDeployment = isDeploymentSpecific ? selectedDeployment : DEFAULT_DEPLOYMENT_TYPE;

      for (const tabPanel of child.content || []) {
        if (tabPanel.type === NODE_NAMES.TAB_PANEL) {
          if (getTabPanelDeploymentTypes(tabPanel.attrs).includes(targetDeployment)) {
            return convertBlocksToText(
              tabPanel.content,
              ragResults,
              variableValues,
              collectionToolMap
            );
          }
        }
      }

      // Fallback: try NEXUS tab
      for (const tabPanel of child.content || []) {
        if (tabPanel.type === NODE_NAMES.TAB_PANEL) {
          if (getTabPanelDeploymentTypes(tabPanel.attrs).includes(DEFAULT_DEPLOYMENT_TYPE)) {
            return convertBlocksToText(
              tabPanel.content,
              ragResults,
              variableValues,
              collectionToolMap
            );
          }
        }
      }

      // Last fallback: first tab
      const firstTab = (child.content || [])[0];
      if (firstTab?.type === NODE_NAMES.TAB_PANEL) {
        return convertBlocksToText(firstTab.content, ragResults, variableValues, collectionToolMap);
      }

      return "";
    }
  }

  return "";
}

/**
 * Converts TipTap JSON content to the text format that the AI will see.
 *
 * ## Conversion Rules:
 * - Section titles are NOT included (skipped)
 * - Each section has a tabContainer (new architecture)
 * - For sections with isDeploymentSpecific: false → uses NEXUS tab
 * - For sections with isDeploymentSpecific: true → uses selectedDeployment tab
 * - Hard breaks (Shift+Enter) → single line break `\n`
 * - New paragraphs (Enter) → double line break `\n\n`
 * - Sections separated by `---`
 *
 * @param json - The TipTap editor JSON content
 * @param selectedDeployment - The deployment type to use for deployment-specific sections (default: NEXUS)
 * @param ragResults - Optional map of collectionName → RAG search results (chat)
 * @param variableValues - Optional map of variableName → runtime value
 * @param collectionToolMap - Optional map of collectionId → tool function name.
 *   The map's PRESENCE signals "voice runtime mode": every collection block
 *   drops from the prompt (no inline RAG, the model learns about the tool via
 *   its `@search_<slug>` skill mention and the registered function declaration).
 *   The map's CONTENTS are no longer consulted by the converter — a future
 *   cleanup will replace this param with an `isVoiceRuntime: boolean`. For now
 *   callers MUST pass a Map (even an empty one) to opt into the voice-mode
 *   drop; passing `undefined` lands in editor preview mode and renders the
 *   `%%name%%` placeholder.
 * @returns The parsed text representation for AI consumption
 *
 * @example
 * // Get AI text for WhatsApp deployment
 * const aiText = convertPromptToAIText(editor.getJSON(), "WHATSAPP")
 *
 * // Get AI text with variable replacement (backend)
 * const variableValues = new Map([["conversationId", "Conversation ID: abc123"]])
 * const aiText = convertPromptToAIText(editor.getJSON(), "NEXUS", undefined, variableValues)
 *
 * // Get AI text for voice with collection → tool hints
 * const toolMap = new Map([["col_123", "Search_FAQ_Fr"]])
 * const aiText = convertPromptToAIText(editor.getJSON(), "PHONE_CALL", undefined, undefined, toolMap)
 */
export function convertPromptToAIText(
  json: JSONContent | null,
  selectedDeployment: string = DEFAULT_DEPLOYMENT_TYPE,
  ragResults?: Map<string, string>,
  variableValues?: Map<string, string>,
  collectionToolMap?: Map<string, string>
): string {
  if (!json || !json.content) return "";

  const sections = json.content.filter((node) => node.type === NODE_NAMES.SECTION);

  return sections
    .map((section) =>
      convertSectionToText(
        section,
        selectedDeployment,
        ragResults,
        variableValues,
        collectionToolMap
      )
    )
    .filter(Boolean)
    .join("\n\n---\n\n");
}
